import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { certificatesAPI, domainsAPI } from '@hpanel/web-common/api/client'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'
import type { Certificate, Domain } from '@hpanel/web-common/api/types'
import { DnsHostingSection } from '../../components/client/DnsHostingSection'
import { DomainLogsPanel } from '../../components/client/DomainLogsPanel'
import { ObservabilityModal } from '../../components/Tenant/Observability/ObservabilityModal'
import {
  Globe,
  Cpu,
  Shield,
  Pause,
  Play,
  AlertTriangle,
  ExternalLink,
  Code2,
  FileText,
  Activity,
  ArrowLeft,
} from 'lucide-react'

/**
 * DomainManagePage — route: /client/domains/:id
 *
 * Replaces the per-domain "Manage" modal (CLAUDE.md §15: a flow with several
 * actions is a routed page, so it survives refresh and the back button).
 *
 *   - shows the domain's state (status, type, created, container, usage)
 *   - suspends / reactivates via domainsAPI.updateStatus
 *   - shows this domain's SSL certificate (status, issuer, expiry) with a
 *     Renew action, and requests a first certificate via certificatesAPI.issue
 *   - links to the DNS / runtime / PHP pages and opens the log / live
 *     observability viewers (read-only overlays, no form to lose)
 *
 * Deliberately NOT here: rename, delete, transfer. Delete stays on the
 * Domains list card.
 */

const statusToneClass: Record<string, string> = {
  active: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  suspended: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  pending_dns:
    'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
}

const certToneClass = (status: string): string => {
  switch (status) {
    case 'issued':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'superseded':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
    case 'expired':
    case 'revoked':
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    default:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
  }
}

/** Format a backend ISO timestamp as a short, locale-friendly date. */
const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const fmtMb = (bytes?: number | null): string => {
  if (bytes == null) return '—'
  return `${Math.round(bytes / 1048576).toLocaleString()} MB`
}

const expiryHint = (expiresAt: string | null): string => {
  if (!expiresAt) return '—'
  const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000)
  if (days < 0) return `expired ${-days}d ago`
  return `${days}d remaining`
}

/** The certificate to show: the newest one that is not superseded. */
const pickCert = (certs: Certificate[], domainId: number): Certificate | null => {
  const mine = certs.filter((c) => c.domain_id === domainId).sort((a, b) => b.id - a.id)
  return mine.find((c) => c.status !== 'superseded') ?? mine[0] ?? null
}

const linkClass =
  'flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors'
const actionButtonClass =
  'w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-800 text-[13px] font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
const cardClass =
  'bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-5'

const errorBox = (testId: string, text: string) => (
  <div
    data-testid={testId}
    role="alert"
    className="mb-3 flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-[12px] text-red-700 dark:text-red-300"
  >
    <AlertTriangle
      className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"
      strokeWidth={1.75}
      aria-hidden="true"
    />
    <span>{text}</span>
  </div>
)

export const DomainManagePage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const domainId = Number(id)
  const validId = Number.isInteger(domainId) && domainId > 0

  const [domain, setDomain] = useState<Domain | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logsOpen, setLogsOpen] = useState(false)
  const [observabilityOpen, setObservabilityOpen] = useState(false)

  const [cert, setCert] = useState<Certificate | null>(null)
  const [certLoading, setCertLoading] = useState(true)
  const [certBusy, setCertBusy] = useState(false)
  const [certError, setCertError] = useState<string | null>(null)
  const [certOk, setCertOk] = useState(false)

  const userId = user?.id

  const loadCert = useCallback(async () => {
    if (!userId || !validId) return
    try {
      const resp = await certificatesAPI.list(userId)
      setCert(pickCert(resp.certificates, domainId))
    } catch (e: unknown) {
      console.error('Loading certificates failed', e)
      setCertError(
        e instanceof Error && e.message ? e.message : 'Failed to load the certificate.',
      )
    } finally {
      setCertLoading(false)
    }
  }, [userId, domainId, validId])

  useEffect(() => {
    if (!userId) return
    if (!validId) {
      setNotFound(true)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    setLoadError(null)
    domainsAPI
      .list(userId)
      .then((resp) => {
        if (cancelled) return
        const found = resp.domains.find((d) => d.id === domainId) ?? null
        setDomain(found)
        setNotFound(found === null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        console.error('Loading domain failed', e)
        setLoadError(
          e instanceof Error && e.message ? e.message : 'Failed to load this domain.',
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId, domainId, validId])

  useEffect(() => {
    setCertError(null)
    setCertLoading(true)
    void loadCert()
  }, [loadCert])

  const backLink = (
    <Link
      to="/client/domains"
      data-testid="manage-domain-back"
      className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
      Back to Domains
    </Link>
  )

  if (loading) {
    return (
      <div data-testid="manage-domain-page" className="space-y-4">
        {backLink}
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Loading domain...
        </div>
      </div>
    )
  }

  if (loadError || notFound || !domain) {
    return (
      <div data-testid="manage-domain-page" className="space-y-4">
        {backLink}
        {loadError ? (
          errorBox('manage-domain-load-error', loadError)
        ) : (
          <div
            data-testid="manage-domain-not-found"
            className="text-center py-12 text-gray-600 dark:text-gray-400"
          >
            Domain not found. It may have been deleted, or it belongs to another account.
          </div>
        )}
      </div>
    )
  }

  const isSuspended = domain.status === 'suspended'

  /** Toggle suspend <-> active; the audit log records the real transition. */
  const handleToggleStatus = async () => {
    setError(null)
    setBusy(true)
    try {
      const target = isSuspended ? 'active' : 'suspended'
      await domainsAPI.updateStatus(domain.id, target)
      setDomain({ ...domain, status: target })
    } catch (e: unknown) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : `Failed to ${isSuspended ? 'reactivate' : 'suspend'} ${domain.name}.`,
      )
    } finally {
      setBusy(false)
    }
  }

  /** Request a first certificate; surface the server's `detail` verbatim on failure. */
  const handleIssueCert = async () => {
    setCertError(null)
    setCertOk(false)
    setCertBusy(true)
    try {
      await certificatesAPI.issue(domain.id)
      setCertOk(true)
      await loadCert()
    } catch (e: unknown) {
      console.error('Certificate issue failed', e)
      setCertError(
        e instanceof Error && e.message
          ? e.message
          : `Could not issue a certificate for ${domain.name}.`,
      )
    } finally {
      setCertBusy(false)
    }
  }

  const handleRenewCert = async () => {
    if (!cert || !userId) return
    setCertError(null)
    setCertOk(false)
    setCertBusy(true)
    try {
      await certificatesAPI.renew(cert.id, userId)
      await loadCert()
    } catch (e: unknown) {
      console.error('Certificate renew failed', e)
      setCertError(
        e instanceof Error && e.message
          ? e.message
          : `Could not renew the certificate for ${domain.name}.`,
      )
    } finally {
      setCertBusy(false)
    }
  }

  return (
    <div data-testid="manage-domain-page" className="space-y-6 max-w-2xl">
      {backLink}

      {/* Header + detail */}
      <div data-testid="manage-domain-panel" className={cardClass}>
        <h1
          data-testid="manage-domain-name"
          className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate"
        >
          {domain.name}
        </h1>
        <div className="mt-2 flex items-center gap-2">
          <span
            data-testid="manage-domain-status"
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${
              statusToneClass[domain.status] ??
              'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
            <span className="capitalize">{domain.status.replace('_', ' ')}</span>
          </span>
          <span className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">
            {domain.domain_type ?? 'primary'}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 pt-4 text-[13px]">
          <dt className="text-gray-500 dark:text-gray-400">Created</dt>
          <dd className="text-gray-900 dark:text-gray-100">{fmtDate(domain.created_at)}</dd>

          <dt className="text-gray-500 dark:text-gray-400">Container</dt>
          <dd className="text-gray-900 dark:text-gray-100 font-mono text-[12px] truncate">
            {domain.container_id != null ? `#${domain.container_id}` : '—'}
          </dd>

          <dt className="text-gray-500 dark:text-gray-400">Disk used</dt>
          <dd className="text-gray-900 dark:text-gray-100">{fmtMb(domain.disk_usage_bytes)}</dd>

          <dt className="text-gray-500 dark:text-gray-400">Bandwidth used</dt>
          <dd className="text-gray-900 dark:text-gray-100">
            {fmtMb(domain.bandwidth_usage_bytes)}
          </dd>

          <dt className="text-gray-500 dark:text-gray-400">DNS zone</dt>
          <dd className="text-gray-900 dark:text-gray-100 font-mono text-[12px] truncate">
            {domain.dns_zone || '—'}
          </dd>

          <dt className="text-gray-500 dark:text-gray-400">Site files</dt>
          <dd
            data-testid="manage-domain-files-path"
            title={domain.files_path ?? undefined}
            className="text-gray-900 dark:text-gray-100 font-mono text-[12px] truncate"
          >
            {domain.files_path || '—'}
          </dd>
        </dl>
      </div>

      {/* Status action */}
      <div className={cardClass}>
        {error && errorBox('manage-domain-error', error)}
        <button
          type="button"
          onClick={handleToggleStatus}
          disabled={busy}
          data-testid="manage-domain-toggle-status"
          className={actionButtonClass}
        >
          {isSuspended ? (
            <>
              <Play className="h-3.5 w-3.5" strokeWidth={1.75} />
              {busy ? 'Reactivating…' : 'Reactivate domain'}
            </>
          ) : (
            <>
              <Pause className="h-3.5 w-3.5" strokeWidth={1.75} />
              {busy ? 'Suspending…' : 'Suspend domain'}
            </>
          )}
        </button>
        <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          {isSuspended
            ? 'Reactivating brings the backing container back online and re-adds the proxy vhost.'
            : 'Suspending stops the container and removes the public vhost. Files are preserved.'}
        </p>
      </div>

      {/* SSL certificate */}
      <div data-testid="manage-domain-cert" className={cardClass}>
        <h2 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-3">
          SSL certificate
        </h2>
        {certError && errorBox('manage-domain-cert-error', certError)}
        {certOk && (
          <div
            data-testid="manage-domain-cert-success"
            role="status"
            className="mb-3 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2 text-[12px] text-green-700 dark:text-green-300"
          >
            Certificate issued for {domain.name}. It is installed and the domain is now
            served over HTTPS.
          </div>
        )}

        {certLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading certificate...</p>
        ) : cert ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] mb-3">
            <dt className="text-gray-500 dark:text-gray-400">Status</dt>
            <dd>
              <span
                data-testid="manage-domain-cert-status"
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${certToneClass(cert.status)}`}
              >
                {cert.status}
              </span>
            </dd>
            <dt className="text-gray-500 dark:text-gray-400">Issuer</dt>
            <dd data-testid="manage-domain-cert-issuer" className="text-gray-900 dark:text-gray-100">
              {cert.issuer}
            </dd>
            <dt className="text-gray-500 dark:text-gray-400">Expires</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              <span data-testid="manage-domain-cert-expiry">{expiryHint(cert.expires_at)}</span>
              {cert.expires_at && (
                <span className="ml-2 text-[11px] text-gray-500 dark:text-gray-400">
                  ({fmtDate(cert.expires_at)})
                </span>
              )}
            </dd>
          </dl>
        ) : (
          !certError && (
            <p
              data-testid="manage-domain-cert-none"
              className="text-sm text-gray-500 dark:text-gray-400 mb-3"
            >
              No certificate yet. Request one below once the domain's DNS points at this server.
            </p>
          )
        )}

        <div className="space-y-2">
          {cert && (
            <button
              type="button"
              onClick={handleRenewCert}
              disabled={busy || certBusy || cert.status === 'superseded'}
              data-testid="manage-domain-cert-renew"
              className={actionButtonClass}
            >
              <Shield className="h-3.5 w-3.5" strokeWidth={1.75} />
              {certBusy ? 'Working…' : 'Renew'}
            </button>
          )}
          {!cert && !certLoading && (
            <button
              type="button"
              onClick={handleIssueCert}
              disabled={busy || certBusy}
              data-testid="manage-domain-issue-cert"
              className={actionButtonClass}
            >
              <Shield className="h-3.5 w-3.5" strokeWidth={1.75} />
              {certBusy ? 'Requesting certificate…' : 'SSL certificate'}
            </button>
          )}
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          Certificates are free and serve the domain over HTTPS. The domain's DNS must already
          point at this server.
        </p>
      </div>

      <DnsHostingSection domainId={domain.id} />

      {/* Quick links */}
      <div className={cardClass}>
        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">
          Configure
        </p>
        <div className="grid grid-cols-1 gap-1.5">
          <Link
            to={`/client/dns?domain=${domain.id}`}
            data-testid="manage-domain-link-dns"
            className={linkClass}
          >
            <Globe className="h-4 w-4 text-gray-500" strokeWidth={1.75} />
            <span className="flex-1">DNS records</span>
            <ExternalLink className="h-3 w-3 text-gray-400" strokeWidth={1.75} />
          </Link>
          <Link
            to={`/client/runtime?container=${domain.container_id}`}
            data-testid="manage-domain-link-runtime"
            className={linkClass}
          >
            <Cpu className="h-4 w-4 text-gray-500" strokeWidth={1.75} />
            <span className="flex-1">Runtime &amp; image</span>
            <ExternalLink className="h-3 w-3 text-gray-400" strokeWidth={1.75} />
          </Link>
          <Link
            to={`/client/php?domain=${domain.id}`}
            data-testid="manage-domain-link-php"
            className={linkClass}
          >
            <Code2 className="h-4 w-4 text-gray-500" strokeWidth={1.75} />
            <span className="flex-1">PHP settings</span>
            <ExternalLink className="h-3 w-3 text-gray-400" strokeWidth={1.75} />
          </Link>
          <button
            type="button"
            onClick={() => setLogsOpen(true)}
            disabled={domain.container_id == null}
            data-testid="manage-domain-link-logs"
            className={`${linkClass} disabled:opacity-40 disabled:cursor-not-allowed text-left`}
          >
            <FileText className="h-4 w-4 text-gray-500" strokeWidth={1.75} />
            <span className="flex-1">View container logs</span>
          </button>
          <button
            type="button"
            onClick={() => setObservabilityOpen(true)}
            disabled={domain.container_id == null}
            data-testid="manage-domain-link-observability"
            className={`${linkClass} disabled:opacity-40 disabled:cursor-not-allowed text-left`}
          >
            <Activity className="h-4 w-4 text-gray-500" strokeWidth={1.75} />
            <span className="flex-1">Observability (live)</span>
          </button>
        </div>
      </div>

      {logsOpen && <DomainLogsPanel domain={domain} onClose={() => setLogsOpen(false)} />}
      {observabilityOpen && (
        <ObservabilityModal
          domainId={domain.id}
          domainName={domain.name}
          onClose={() => setObservabilityOpen(false)}
        />
      )}
    </div>
  )
}
